import { Button, Flex, FormControl, Grid, Spinner, Text } from '@chakra-ui/react';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMoralis, useMoralisCloudFunction, useMoralisQuery } from 'react-moralis';
import { placeholderUrl } from 'src/config';
import { useBlockchainProvider } from 'src/providers/Blockchain/blockchainProvider';
import { FORM_CONFIG } from '../../CartridgeForm/formConfig';
import { cartridges } from '../../CartridgeForm/Pages/SetCartridgeType/cartridges';
import { CartridgeInfoType } from '../../CartridgeForm/Pages/SetCartridgeType/setCartridgeType';
import { availableCategories, availablePlatforms } from '../../CartridgeForm/Pages/SetMetadata/setMetadata';
import { AddScreenshots } from '../../CartridgeForm/Pages/SetScreenshotsAndVideo/AddScreenshots/addScreenshots';
import { getValidationSchema } from '../../CartridgeForm/schema';
import { ColorInput } from '../../inputs/InputField/colorInput';
import { InputField } from '../../inputs/InputField/inputField';
import { TextareaField } from '../../inputs/InputField/textareaField';
import { TagSelector } from '../../inputs/TagSelector/tagSelector';
import { DropdownItem } from '../DropdownItem/dropdownItem';
import Moralis from 'moralis-v1';

export const MetadataTab = ({ address }: { address: string }) => {
	const { t } = useTranslation();
	const { values } = useFormikContext();
	const [selectedCartridge, setSelectedCartridge] = useState<CartridgeInfoType>(cartridges[1]);
	const [dataChanged, setIfDataChanged] = useState<boolean>(false);

	const currentSchema = useMemo(
		() => getValidationSchema(2, cartridges[FORM_CONFIG.DEFAULT_CARTRIDGE_NUMBER], 100000, t),
		[t]
	);

	const { account } = useBlockchainProvider();
	const moralis = useMoralis();
	const [isReady, setIsReady] = useState<boolean>(false);

	const [game, setGame] = useState({
		colors: { actionColor: '', textColor: '', backgroundColor: '' },
		description: '',
		email: '',
		gameUrl: '',
		gamepaper: '',
		media: { coverImageUrl: '', logoUrl: '', banner: '' },
		screenshots: [],
		socials: { facebook: '', twitter: '', youtube: '', discord: '' },
		video: '',
		genre: [],
		platforms: [],
	});

	const [gameId, setGameId] = useState('');

	const { data, error, isLoading } = useMoralisQuery('Games', query =>
		query.equalTo('creator', account.toLowerCase())
	);

	useEffect(() => {
		if (data.length > 0) {
			if (data[0].id !== undefined)
				data.map(el => {
					if (el.id === address) {
						setGameId(el.id);
						setGame({
							colors: el.attributes.colors,
							description: el.attributes.description,
							email: el.attributes.email ? el.attributes.email : '',
							gameUrl: el.attributes.gameUrl,
							gamepaper: el.attributes.gamepaper,
							media: el.attributes.media,
							screenshots: el.attributes.screenshots,
							socials: el.attributes.socials,
							video: el.attributes.video,
							genre: el.attributes.genres,
							platforms: el.attributes.platforms,
						});
					}
				});
		}
	}, [data]);

	const {
		data: queryData,
		fetch: queryFetch,
		isFetching: queryIsFetching,
		isLoading: queryIsLoading,
	} = useMoralisCloudFunction('updateMetadata', {});

	const saveMetadata = async (metadataObject: any) => {
		try {
			const file = new moralis.Moralis.File('metadata.json', { base64: btoa(JSON.stringify(metadataObject)) });
			const res = await file.saveIPFS();
			//console.log(res._url);
			return res._url;
		} catch (err: any) {
			alert(err);
			return 'No url';
		}
	};

	useEffect(() => {
		if (game.description !== '') setIsReady(true);
	}, [game]);

	return !isReady ? (
		<Spinner />
	) : (
		<Grid>
			<Formik
				initialValues={game}
				onSubmit={async (values, actions) => {
					setTimeout(() => {
						actions.setSubmitting(false);
					}, 1000);
					setGame(values);

					// console.log(values);
					const url = await saveMetadata(values);
					//alert(url);

					const user = Moralis.User.current();
					await queryFetch({
						params: {
							gameId: address,
							metadataUri: url,
							user: {
								address: user?.attributes.ethAddress,
								signature: user?.attributes.authData.moralisEth.signature,
							},
						},
					});

					console.log('data:', queryData);
				}}
			>
				{props => (
					<Form
						onChange={(e: any) => {
							setIfDataChanged(true);
						}}
					>
						<Flex
							flexDirection="column"
							gridGap="10px"
						>
							<DropdownItem heading={t('form.headers.media')}>
								<Flex
									flexDirection="column"
									alignItems="center"
									w="100%"
								>
									<Field name="media.coverImageUrl">
										{({ field, form }: { field: any; form: any }) => (
											<FormControl>
												<InputField
													{...field}
													warningText="1600 x 1200 px"
													label={t('form.fields.coverURL')}
													placeholder={placeholderUrl('cartridge-cover.jpg')}
													leftText="URL"
												/>
											</FormControl>
										)}
									</Field>
									<Field name="media.logoUrl">
										{({ field, form }: { field: any; form: any }) => (
											<FormControl>
												<InputField
													{...field}
													warningText="400 x 400 px"
													label={t('form.fields.logoImage')}
													placeholder={placeholderUrl('cartridge-logo.jpg')}
													leftText="URL"
												/>
											</FormControl>
										)}
									</Field>
									<Field name="media.banner">
										{({ field, form }: { field: any; form: any }) => (
											<FormControl>
												<InputField
													{...field}
													warningText="1200 x 1200 px"
													label={t('form.fields.banner')}
													placeholder={placeholderUrl('banner-big.jpg')}
													leftText="URL"
												/>
											</FormControl>
										)}
									</Field>
								</Flex>
							</DropdownItem>

							<DropdownItem heading={t('form.headers.profileColors.title')}>
								<Flex
									flexDirection="column"
									gridGap="30px"
									alignItems="center"
								>
									<Text
										fontSize="14px"
										fontWeight="300"
										opacity="0.7"
										textAlign="center"
									>
										{t('form.headers.profileColors.description')}
									</Text>
									<Flex
										gridGap="20px"
										alignItems="flex-end"
										width="100%"
									>
										<Field name="colors.actionColor">
											{({ field, form }: { field: any; form: any }) => {
												return (
													<FormControl>
														<ColorInput
															hexColor={field.value}
															label={t('form.fields.actionColor')}
															name="colors.actionColor"
															disabled={false}
														/>
													</FormControl>
												);
											}}
										</Field>
										<Field name="colors.textColor">
											{({ field, form }: { field: any; form: any }) => (
												<FormControl>
													<ColorInput
														hexColor={field.value}
														label={t('form.fields.textColor')}
														name="colors.textColor"
														disabled={false}
													/>
												</FormControl>
											)}
										</Field>
										<Field name="colors.backgroundColor">
											{({ field, form }: { field: any; form: any }) => (
												<FormControl>
													<ColorInput
														hexColor={field.value}
														label={t('form.fields.backgroundColor')}
														name="colors.backgroundColor"
														disabled={false}
													/>
												</FormControl>
											)}
										</Field>
									</Flex>
								</Flex>
							</DropdownItem>

							<DropdownItem heading={t('form.headers.gameUrl.title')}>
								<Flex
									flexDirection="column"
									gridGap="30px"
									alignItems="center"
								>
									<Text
										fontSize="14px"
										fontWeight="300"
										opacity="0.7"
										textAlign="center"
									>
										{t('form.headers.gameUrl.description')}
									</Text>
									<Field name="gameUrl">
										{({ field, form }: { field: any; form: any }) => (
											<FormControl>
												<InputField
													{...field}
													label={t('form.fields.gameURL')}
													placeholder={placeholderUrl('game')}
													leftText="URL"
												/>
											</FormControl>
										)}
									</Field>
								</Flex>
							</DropdownItem>

							<DropdownItem heading={t('yourGames.metadata.tags')}>
								<Flex
									flexDirection="column"
									gridGap="20px"
								>
									<TagSelector
										tags={availableCategories}
										label={t('form.headers.tags.category.title')}
										name="genre"
										selected={game.genre}
									/>
									<TagSelector
										tags={availablePlatforms}
										label={t('form.headers.tags.platforms.title')}
										name="platforms"
										selected={game.platforms}
									/>
								</Flex>
							</DropdownItem>

							<DropdownItem heading={t('form.fields.description')}>
								<Flex
									flexDirection="column"
									gridGap="20px"
									width="100%"
								>
									<Field name="description">
										{({ field, form }: { field: any; form: any }) => (
											<FormControl>
												<TextareaField
													{...field}
													placeholder=""
													label={t('form.fields.description')}
												/>
											</FormControl>
										)}
									</Field>
								</Flex>
							</DropdownItem>

							<DropdownItem heading="Social media">
								<Flex
									flexDirection="column"
									gridGap="20px"
									width="100%"
								>
									<Field name="socials.facebook">
										{({ field, form }: { field: any; form: any }) => (
											<FormControl>
												<InputField
													{...field}
													fixedLeftWidth
													leftText="Facebook"
												/>
											</FormControl>
										)}
									</Field>
									<Field name="socials.twitter">
										{({ field, form }: { field: any; form: any }) => (
											<FormControl>
												<InputField
													{...field}
													fixedLeftWidth
													leftText="Twitter"
												/>
											</FormControl>
										)}
									</Field>
									<Field name="socials.youtube">
										{({ field, form }: { field: any; form: any }) => (
											<FormControl>
												<InputField
													{...field}
													fixedLeftWidth
													leftText="Youtube"
												/>
											</FormControl>
										)}
									</Field>
									<Field name="socials.discord">
										{({ field, form }: { field: any; form: any }) => (
											<FormControl>
												<InputField
													{...field}
													fixedLeftWidth
													leftText="Discord"
												/>
											</FormControl>
										)}
									</Field>
								</Flex>
							</DropdownItem>

							<DropdownItem heading={t('form.fields.videoPreview')}>
								<Flex
									flexDirection="column"
									gridGap="20px"
									width="100%"
								>
									<Field name="video">
										{({ field, form }: { field: any; form: any }) => (
											<FormControl>
												<InputField
													{...field}
													leftText="Youtube URL"
												/>
											</FormControl>
										)}
									</Field>
								</Flex>
							</DropdownItem>

							<DropdownItem heading={t('form.fields.screenshots')}>
								<Flex
									flexDirection="column"
									gridGap="20px"
									width="100%"
								>
									{game.screenshots.map((el, id) => (
										<Field
											name="screenshots"
											key={id}
										>
											{({ field, form }: { field: any; form: any }) => (
												<FormControl>
													<AddScreenshots name="screenshots" />
												</FormControl>
											)}
										</Field>
									))}
								</Flex>
							</DropdownItem>

							<Button
								mt={4}
								colorScheme="teal"
								isLoading={props.isSubmitting}
								type="submit"
								disabled={!dataChanged}
							>
								{t('yourGames.metadata.update')}
							</Button>
						</Flex>
					</Form>
				)}
			</Formik>
		</Grid>
	);
};
